import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Location, To } from "react-router-dom";
import type { GridPaginationModel, GridSortItem, GridSortModel } from "@mui/x-data-grid";

import {
  consumeTableReset,
  readLastTableState,
  readVisitTableState,
  writeTableState,
  type TableStateScope,
} from "../utils/tableStateStorage";

/** Any value a dropdown filter can hold once it has been through JSON: ids, id lists, picker option objects. */
export type TableDropdownValue = string | number | boolean | null | TableDropdownValue[] | { [key: string]: TableDropdownValue };

/** Dropdown filters by key, e.g. `{ categoryId: "" }` or `{ assignees: [], assigneeIds: "" }`. */
export type TableDropdowns = Record<string, TableDropdownValue>;

/**
 * The date range as the component builds it from the picker (ISO timestamps or `YYYY-MM-DD`); stored as-is so the
 * request params stay exactly what the component sends today.
 */
export interface TableDateRange {
  startDate: string;
  endDate: string;
}

export interface TableStateDefaults<TFilter, TDropdowns extends TableDropdowns = TableDropdowns> {
  filter?: TFilter;
  dropdowns?: TDropdowns;
  sortModel?: GridSortItem[];
  pageSize?: number;
}

export interface UsePersistedTableStateOptions<TFilter, TDropdowns extends TableDropdowns> {
  /** Unique among the tables rendered on one route, e.g. "workers", "statement", "project-works". */
  tableId: string;
  /** What the table shows on a fresh visit. */
  defaults: TableStateDefaults<TFilter, TDropdowns>;
  /** The filter options; a saved filter outside this list falls back to the default. */
  filterValues?: readonly TFilter[];
}

/** A value, or a function of the current value — the same contract as React's `useState` setters. */
export type TableStateUpdate<TValue> = TValue | ((current: TValue) => TValue);

export interface PersistedTableState<TFilter, TDropdowns extends TableDropdowns = TableDropdowns> {
  filter: TFilter;
  /** Resets to the first page. */
  setFilter: (filter: TFilter) => void;
  dropdowns: TDropdowns;
  /** Merges the given dropdown values and resets to the first page. */
  setDropdowns: (patch: Partial<TDropdowns>) => void;
  searchText: string;
  /** Resets to the first page. */
  setSearchText: (searchText: string | undefined) => void;
  sortModel: GridSortItem[];
  setSortModel: (sortModel: GridSortModel) => void;
  paginationModel: GridPaginationModel;
  setPaginationModel: (update: TableStateUpdate<GridPaginationModel>) => void;
  /** `null` until the date picker reports its initial range. */
  dateRange: TableDateRange | null;
  /** Resets to the first page. */
  setDateRange: (dateRange: TableDateRange | null) => void;
  /** The saved toggle only; components must still AND it with their ledger permission. */
  isLedgerMode: boolean;
  /** Resets to the first page. */
  setLedgerMode: (update: TableStateUpdate<boolean>) => void;
}

interface TableStateValues<TFilter, TDropdowns extends TableDropdowns> {
  filter: TFilter;
  dropdowns: TDropdowns;
  searchText: string;
  sortModel: GridSortItem[];
  paginationModel: GridPaginationModel;
  dateRange: TableDateRange | null;
  isLedgerMode: boolean;
}

interface TableRouterState {
  tableVisitKey?: string;
  restoreTableState?: boolean;
}

const DEFAULT_PAGE_SIZE = 10;
// DataGrid (MIT) throws for page sizes above 100.
const MAX_PAGE_SIZE = 100;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const routerState = (location: Location): TableRouterState => (isRecord(location.state) ? location.state : {});

// React Router's key for entries it did not create: the first page load, and a URL typed or opened in the same tab.
const UNTRACKED_LOCATION_KEY = "default";

/**
 * Gives an untracked history entry its own visit key, stored in the entry's `history.state` (under `usr`, where
 * React Router keeps navigation state) so a refresh finds it again. Without this, every such entry would share the
 * key "default", and typing a list's URL would restore the state of the page the app was first loaded on.
 */
const getUntrackedEntryVisitKey = (): string => {
  try {
    const entryState: unknown = window.history.state;
    const baseState = isRecord(entryState) ? entryState : {};
    const userState = isRecord(baseState.usr) ? baseState.usr : {};
    if (typeof userState.tableVisitKey === "string") return userState.tableVisitKey;

    const tableVisitKey = `entry-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.history.replaceState({ ...baseState, usr: { ...userState, tableVisitKey } }, "");
    return tableVisitKey;
  } catch {
    return UNTRACKED_LOCATION_KEY;
  }
};

/**
 * Identifies the current visit (history entry). A tab switch through `useTabNavigate` replaces the entry, which
 * gives it a new `location.key`, so the visit's original key is carried forward in router state.
 */
const getVisitKey = (location: Location): string => {
  const { tableVisitKey } = routerState(location);
  if (typeof tableVisitKey === "string") return tableVisitKey;
  return location.key === UNTRACKED_LOCATION_KEY ? getUntrackedEntryVisitKey() : location.key;
};

const isSortItem = (value: unknown): value is GridSortItem =>
  isRecord(value) && typeof value.field === "string" && (value.sort === "asc" || value.sort === "desc");

const isDateString = (value: unknown): value is string => typeof value === "string" && !Number.isNaN(Date.parse(value));

const isDateRange = (value: unknown): value is TableDateRange =>
  isRecord(value) && isDateString(value.startDate) && isDateString(value.endDate);

const isPageIndex = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 0;

const isPageSize = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= 1 && (value as number) <= MAX_PAGE_SIZE;

const buildDefaults = <TFilter, TDropdowns extends TableDropdowns>(
  defaults: TableStateDefaults<TFilter, TDropdowns>,
): TableStateValues<TFilter, TDropdowns> => ({
  filter: defaults.filter as TFilter,
  // Only absent when the table declares no dropdowns, in which case TDropdowns is the open record.
  dropdowns: { ...defaults.dropdowns } as TDropdowns,
  searchText: "",
  sortModel: defaults.sortModel ? [...defaults.sortModel] : [],
  paginationModel: { page: 0, pageSize: defaults.pageSize ?? DEFAULT_PAGE_SIZE },
  dateRange: null,
  isLedgerMode: false,
});

/**
 * Restores the dropdowns the table declares, ignoring any other saved keys. Their values are not checked further:
 * they are only ever sent back as request filters, which the backend validates and authorises.
 */
const restoreDropdowns = <TDropdowns extends TableDropdowns>(saved: unknown, fallback: TDropdowns): TDropdowns => {
  if (!isRecord(saved)) return fallback;
  const restored = { ...fallback };
  (Object.keys(fallback) as (keyof TDropdowns & string)[]).forEach((key) => {
    // Written by writeTableState from this same shape, so a present key holds that dropdown's value.
    if (key in saved) restored[key] = saved[key] as TDropdowns[typeof key];
  });
  return restored;
};

/** Rebuilds state from storage, keeping only fields that are still valid and falling back to defaults for the rest. */
const restoreValues = <TFilter, TDropdowns extends TableDropdowns>(
  saved: unknown,
  fallback: TableStateValues<TFilter, TDropdowns>,
  filterValues: readonly TFilter[] | undefined,
): TableStateValues<TFilter, TDropdowns> | null => {
  if (!isRecord(saved)) return null;

  const isValidFilter = filterValues
    ? filterValues.some((value) => value === saved.filter)
    : saved.filter === null || typeof saved.filter === typeof fallback.filter;
  const savedPagination = isRecord(saved.paginationModel) ? saved.paginationModel : {};

  return {
    filter: "filter" in saved && isValidFilter ? (saved.filter as TFilter) : fallback.filter,
    dropdowns: restoreDropdowns(saved.dropdowns, fallback.dropdowns),
    searchText: typeof saved.searchText === "string" ? saved.searchText : fallback.searchText,
    sortModel: Array.isArray(saved.sortModel) && saved.sortModel.every(isSortItem) ? saved.sortModel : fallback.sortModel,
    paginationModel: {
      page: isPageIndex(savedPagination.page) ? savedPagination.page : 0,
      pageSize: isPageSize(savedPagination.pageSize) ? savedPagination.pageSize : fallback.paginationModel.pageSize,
    },
    dateRange: isDateRange(saved.dateRange) ? saved.dateRange : null,
    isLedgerMode: saved.isLedgerMode === true,
  };
};

/**
 * Where a table's (or page value's) state comes from when it mounts or moves to another visit:
 * 1. a record was just created (`markTableReset`) → defaults, once per table, so the new record is not hidden;
 * 2. this visit already has saved state (Back/Forward, refresh, tab switch) → that state;
 * 3. the navigation asked to restore (`RESTORE_TABLE_STATE`) → the screen's last state;
 * 4. otherwise (any forward navigation) → defaults.
 * A forward navigation always creates a new history entry, so step 2 can only match a visit the user has seen.
 */
const loadInitialValue = <TValue>(
  scope: TableStateScope,
  location: Location,
  fallback: TValue,
  restore: (saved: unknown) => TValue | null,
): TValue => {
  if (consumeTableReset(scope)) return fallback;

  const visitValue = restore(readVisitTableState(scope));
  if (visitValue !== null) return visitValue;

  if (routerState(location).restoreTableState) return restore(readLastTableState(scope)) ?? fallback;
  return fallback;
};

const isSameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const scopeId = ({ visitKey, path, tableId }: TableStateScope) => JSON.stringify([visitKey, path, tableId]);

/**
 * Keeps a value bound to the current visit. Navigating between two visits of the same route (the sidebar link to
 * the page already open, or one record to another) keeps the component mounted, so the value is reloaded for the
 * new visit during render instead of carrying the previous visit's state over.
 */
const useVisitBoundState = <TValue>(tableId: string, load: (scope: TableStateScope, location: Location) => TValue) => {
  const location = useLocation();
  const scope: TableStateScope = { visitKey: getVisitKey(location), path: location.pathname, tableId };
  const currentScopeId = scopeId(scope);

  const [bound, setBound] = useState(() => ({ scope, scopeId: currentScopeId, value: load(scope, location) }));
  if (bound.scopeId !== currentScopeId) {
    const next = { scope, scopeId: currentScopeId, value: load(scope, location) };
    setBound(next);
    return [next, setBound] as const;
  }
  return [bound, setBound] as const;
};

/**
 * Filter, dropdown, search, sort, pagination, date-range and ledger-mode state for an `MTable`, remembered in
 * sessionStorage for each visit of each screen.
 *
 * - **Back restores**: returning to a screen (browser Back, `navigate(-1)`, refresh) shows the table as it was.
 * - **Forward is fresh**: opening a screen from a link, menu or row click shows the defaults.
 * - **Edit/Cancel restores, create resets**: see `RESTORE_TABLE_STATE` and `markTableReset`.
 * - **Each table is independent**: state is keyed by visit, route path and `tableId`.
 */
export const usePersistedTableState = <TFilter = undefined, TDropdowns extends TableDropdowns = TableDropdowns>({
  tableId,
  defaults,
  filterValues,
}: UsePersistedTableStateOptions<TFilter, TDropdowns>): PersistedTableState<TFilter, TDropdowns> => {
  const [bound, setBound] = useVisitBoundState(tableId, (scope, location) => {
    const fallback = buildDefaults(defaults);
    return loadInitialValue(scope, location, fallback, (saved) => restoreValues(saved, fallback, filterValues));
  });
  const values = bound.value;

  useEffect(() => {
    writeTableState(bound.scope, bound.value);
  }, [bound]);

  const setValues = useCallback(
    (reducer: (current: TableStateValues<TFilter, TDropdowns>) => TableStateValues<TFilter, TDropdowns>) =>
      setBound((current) => {
        const value = reducer(current.value);
        return value === current.value ? current : { ...current, value };
      }),
    [setBound],
  );

  const update = useCallback(
    (patch: Partial<TableStateValues<TFilter, TDropdowns>>, resetPage = false) => {
      setValues((current) => {
        const changed = Object.entries(patch).some(([key, value]) => !isSameJson(current[key as keyof typeof current], value));
        if (!changed) return current;
        return {
          ...current,
          ...patch,
          ...(resetPage && { paginationModel: { ...current.paginationModel, page: 0 } }),
        };
      });
    },
    [setValues],
  );

  const setFilter = useCallback((filter: TFilter) => update({ filter }, true), [update]);
  const setDropdowns = useCallback(
    (patch: Partial<TDropdowns>) =>
      setValues((current) => {
        const dropdowns = { ...current.dropdowns, ...patch };
        if (isSameJson(current.dropdowns, dropdowns)) return current;
        return { ...current, dropdowns, paginationModel: { ...current.paginationModel, page: 0 } };
      }),
    [setValues],
  );
  const setSearchText = useCallback((searchText: string | undefined) => update({ searchText: searchText ?? "" }, true), [update]);
  const setSortModel = useCallback((sortModel: GridSortModel) => update({ sortModel: [...sortModel] }), [update]);
  const setPaginationModel = useCallback(
    (paginationUpdate: TableStateUpdate<GridPaginationModel>) =>
      setValues((current) => {
        const { page, pageSize } =
          typeof paginationUpdate === "function" ? paginationUpdate(current.paginationModel) : paginationUpdate;
        if (page === current.paginationModel.page && pageSize === current.paginationModel.pageSize) return current;
        return { ...current, paginationModel: { page, pageSize } };
      }),
    [setValues],
  );
  const setDateRange = useCallback((dateRange: TableDateRange | null) => update({ dateRange }, true), [update]);
  const setLedgerMode = useCallback(
    (ledgerUpdate: TableStateUpdate<boolean>) =>
      setValues((current) => {
        const isLedgerMode = typeof ledgerUpdate === "function" ? ledgerUpdate(current.isLedgerMode) : ledgerUpdate;
        if (isLedgerMode === current.isLedgerMode) return current;
        return { ...current, isLedgerMode, paginationModel: { ...current.paginationModel, page: 0 } };
      }),
    [setValues],
  );

  return useMemo(
    () => ({
      ...values,
      setFilter,
      setDropdowns,
      setSearchText,
      setSortModel,
      setPaginationModel,
      setDateRange,
      setLedgerMode,
    }),
    [values, setFilter, setDropdowns, setSearchText, setSortModel, setPaginationModel, setDateRange, setLedgerMode],
  );
};

/**
 * A single value remembered the same way as table state — e.g. a page's active tab when the tab is not in the URL,
 * or a filter held by the page rather than its table. It must survive JSON. `isValid` guards against a saved value
 * that no longer applies (e.g. a tab the user lost access to); by default only the default's type is checked.
 */
export const usePersistedPageValue = <TValue extends TableDropdownValue>(
  valueId: string,
  defaultValue: TValue,
  isValid: (value: unknown) => value is TValue = (value): value is TValue =>
    value !== null && typeof value === typeof defaultValue && Array.isArray(value) === Array.isArray(defaultValue),
): [TValue, (value: TValue) => void] => {
  const [bound, setBound] = useVisitBoundState(`value:${valueId}`, (scope, location) =>
    loadInitialValue(scope, location, defaultValue, (saved) => (isValid(saved) ? saved : null)),
  );

  useEffect(() => {
    writeTableState(bound.scope, bound.value);
  }, [bound]);

  const setValue = useCallback((value: TValue) => setBound((current) => ({ ...current, value })), [setBound]);

  return [bound.value, setValue];
};

/**
 * Navigates between tabs of the same page (e.g. `?tab=works`) without adding a history entry, while keeping the
 * visit's identity so the tables on each tab keep their remembered state.
 */
export const useTabNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (to: To) => {
      const nextState: TableRouterState & Record<string, unknown> = {
        ...(isRecord(location.state) ? location.state : {}),
        tableVisitKey: getVisitKey(location),
      };
      // A restore request applies to the tables the redirect landed on, not to tables on tabs opened afterwards.
      delete nextState.restoreTableState;
      navigate(to, { replace: true, state: nextState });
    },
    [navigate, location],
  );
};
